import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { getNewUITexture } from "../../../../assets";
import {
  FONT_CONFIG,
  TEXT_STYLE_CONFIG,
  PixiSettings,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { closeBonusBuy } from "../../../../State/actions";
import {
  selectBonusBuyActive,
  selectCurrency,
  selectBalance,
  selectIsSpinning,
  selectCounterAnimationFinished,
  selectCriticalAnimationPlaying,
  selectBonusBuyMultiplier,
  selectBonusBuyExtraMultiplier,
} from "../../../../State/selectors";
import { formatToCustomLocale } from "../../../../utilities";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";
import BonusBetSelector from "../Displays/BonusBetSelector";

// ── Overlay ──────────────────────────────────────────────────────────────────
const OVERLAY_ALPHA = 0.7;
const OVERLAY_COLOR = 0x000000;
const OVERLAY_FADE_MS = 200;

// ── Layout config ───────────────────────────────────────────────────────────
// Card dimensions come from popup_frame_bonus texture (native width/height).
// All ratios below are relative to cardWidth so everything scales dynamically.
const POPUP_CONFIG = {
  // Ratios relative to cardWidth (cardWidth derived from texture)
  cardGapRatio: 0.05, // Gap between cards (landscape/desktop)
  cardGapPortraitRatio: 0.075, // Gap between cards (portrait)
  paddingTopRatio: 0.05, // Inner content padding (top)
  paddingBottomRatio: 0.07, // Inner content padding (bottom)
  titleFontRatio: 0.07, // Title font size
  subtitleFontRatio: 0.055, // Subtitle font size
  titleSubGapRatio: 0.075, // Gap between title and subtitle
  imageBottomGapRatio: 0.053, // Gap below image
  buyButtonWidthRatio: 0.6, // Buy button width
  buyFontRatio: 0.069, // Buy button text
  priceFontRatio: 0.065, // Price text
  betSelectorGapRatio: 0.14, // Gap between bet selector and cards (landscape/desktop)
  betSelectorGapPortraitRatio: 0.29, // Gap between bet selector and cards (portrait)
  betSelectorScaleRatio: 0.575, // Bet selector size relative to popupScale
  closeButtonOffsetRatio: 0.05, // Close button offset (% of viewport height)

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.76,
        yOffset: 0.45,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.075,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.75,
        yOffset: 0.475,
        betSelectorScaleRatio: 0.55,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.075,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.5,
        yOffset: 0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.3,
        yOffset: 0,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.15, yOffset: 0.02 },
  },
};

// Measures `text` at `idealFontSize` (single line) and shrinks it (in 2px
// steps) so it fits within `maxWidth`, or returns the ideal size unshrunk.
// Shared by the title and description fit calculations below, and reused by
// the parent (to compute a size shared across both cards) and by BuyCard
// itself (fallback when no shared size is passed in).
const fitFontSizeToWidth = (text, idealFontSize, maxWidth, fontWeight = "bold") => {
  const tempStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: idealFontSize,
    fontWeight,
    wordWrap: false,
  });
  const metrics = TextMetrics.measureText(text, tempStyle);
  if (metrics.width > maxWidth) {
    const scale = maxWidth / metrics.width;
    return Math.round((idealFontSize * scale) / 2) * 2;
  }
  return idealFontSize;
};

// Bonus tier definitions — multipliers come from Redux (populated by API init)
const BONUS_TIER_CONFIGS = [
  {
    key: "zeus_respins",
    titleKey: "bonus_buy_zeus_respins_title",
    descKey: "bonus_buy_zeus_respins_description",
    featureLevel: "1",
    featureImage: "zeus_respins",
    imageScale: 1.075,
  },
  {
    key: "zeus_respins_extra",
    titleKey: "bonus_buy_zeus_respins_extra_title",
    descKey: "bonus_buy_zeus_respins_extra_description",
    featureLevel: "2",
    featureImage: "zeus_respins_features",
    imageScale: 0.9,
    imageYOffset: -0.035,
  },
];

// ═════════════════════════════════════════════════════════════════════════════
// BuyCard — a single bonus option card with title, subtitle, image, buy button
// ═════════════════════════════════════════════════════════════════════════════
const BuyCard = ({
  tier,
  cardWidth,
  cardHeight,
  currentBet,
  currency,
  canAfford,
  onBuy,
  sharedTitleFontSize,
  sharedSubtitleFontSize,
  sharedTitleAreaHeight,
}) => {
  const { t } = useTranslation();
  const { buttonRef, handlePointerOver, handlePointerOut, cleanupFilters } =
    useButtonBrightness();

  const isProcessingRef = useRef(false);
  const buyButtonContainerRef = useRef(null);

  // Clean up filters on unmount
  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // ── All dimensions derived from cardWidth ───────────────────────────────
  const paddingTop = cardWidth * POPUP_CONFIG.paddingTopRatio;
  const paddingBottom = cardWidth * POPUP_CONFIG.paddingBottomRatio;
  const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio; // sides use bottom ratio
  const contentWidth = cardWidth - padding * 2;

  // ── Text styles ─────────────────────────────────────────────────────────
  const dpr = window.devicePixelRatio || 1;
  const idealTitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
  const idealSubtitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
  const titleSubGap = cardWidth * POPUP_CONFIG.titleSubGapRatio;

  // Auto-scale title/description font to fit within card content width. When
  // a shared size is provided (computed across all cards by the parent), use
  // it so every card's text renders at the same size — otherwise a long title
  // or description on one card shrinks while a short one on the other stays
  // at full size, making the two look mismatched.
  const ownTitleFontSize = useMemo(
    () =>
      fitFontSizeToWidth(
        t(tier.titleKey).toUpperCase(),
        idealTitleFontSize,
        contentWidth,
        "bold",
      ),
    [idealTitleFontSize, contentWidth, t, tier.titleKey],
  );
  const titleFontSize = sharedTitleFontSize ?? ownTitleFontSize;

  const ownSubtitleFontSize = useMemo(
    () =>
      fitFontSizeToWidth(
        t(tier.descKey),
        idealSubtitleFontSize,
        contentWidth,
        "500",
      ),
    [idealSubtitleFontSize, contentWidth, t, tier.descKey],
  );
  const subtitleFontSize = sharedSubtitleFontSize ?? ownSubtitleFontSize;
  const imageBottomGap = cardWidth * POPUP_CONFIG.imageBottomGapRatio;

  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        resolution: dpr,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [titleFontSize, contentWidth, dpr],
  );

  const subtitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: subtitleFontSize,
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: Math.round(subtitleFontSize * 1.2),
        resolution: dpr,
      }),
    [subtitleFontSize, contentWidth, dpr],
  );

  // ── Textures ────────────────────────────────────────────────────────────
  const featureTexture = useMemo(
    () => PIXI.Assets.get(tier.featureImage),
    [tier.featureImage],
  );
  const buttonTexture = useMemo(() => getNewUITexture("button_bonus_card"), []);
  const buttonDisabledTexture = useMemo(
    () => getNewUITexture("button_bonus_card_disabled"),
    [],
  );
  const cardBgTexture = useMemo(
    () => getNewUITexture("bonus_card_background"),
    [],
  );

  // ── Button config (all relative to cardWidth) ──────────────────────────
  const btnW = cardWidth * POPUP_CONFIG.buyButtonWidthRatio;
  const btnAR = buttonTexture
    ? buttonTexture.width / buttonTexture.height
    : 270 / 90;
  const btnH = btnW / btnAR;
  const buyFontSize =
    Math.round((cardWidth * POPUP_CONFIG.buyFontRatio) / 2) * 2;
  const priceFontSize =
    Math.round((cardWidth * POPUP_CONFIG.priceFontRatio) / 2) * 2;

  const cost = currentBet * tier.multiplier;
  const priceText = `${formatToCustomLocale(cost)} ${currency}`;

  // ── Auto-scale BUY + price text to fit button bounds ────────────────────
  const maxTextWidth = btnW * 0.9; // 90% of button width
  const maxTextHeight = btnH * 0.85; // 85% of button height for both texts combined
  const textGap = btnH * 0.05; // small gap between the two lines

  const { scaledBuyFontSize, scaledPriceFontSize } = useMemo(() => {
    const buyText = t("common:buy").toUpperCase();

    let bFS = buyFontSize;
    let pFS = priceFontSize;

    const measure = (text, size) => {
      const s = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
      });
      return TextMetrics.measureText(text, s);
    };

    // First pass: scale each for width (widest single line)
    let buyM = measure(buyText, bFS);
    if (buyM.width > maxTextWidth) {
      bFS = Math.round((bFS * (maxTextWidth / buyM.width)) / 2) * 2;
    }
    let priceM = measure(priceText, pFS);
    if (priceM.width > maxTextWidth) {
      pFS = Math.round((pFS * (maxTextWidth / priceM.width)) / 2) * 2;
    }

    // Second pass: check combined height (accounts for wrapped multi-line text)
    buyM = measure(buyText, bFS);
    priceM = measure(priceText, pFS);
    const combinedH = buyM.height + textGap + priceM.height;
    if (combinedH > maxTextHeight) {
      const shrink = maxTextHeight / combinedH;
      bFS = Math.round((bFS * shrink) / 2) * 2;
      pFS = Math.round((pFS * shrink) / 2) * 2;
    }

    return { scaledBuyFontSize: bFS, scaledPriceFontSize: pFS };
  }, [
    buyFontSize,
    priceFontSize,
    priceText,
    maxTextWidth,
    maxTextHeight,
    textGap,
    t,
  ]);

  const buyTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledBuyFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledBuyFontSize, maxTextWidth, dpr],
  );

  const priceTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledPriceFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledPriceFontSize, maxTextWidth, dpr],
  );

  // ── Layout calculation (all relative to card frame) ────────────────────
  const titleText = t(tier.titleKey).toUpperCase();
  const subtitleText = t(tier.descKey);

  const titleMetrics = useMemo(
    () => TextMetrics.measureText(titleText, titleStyle),
    [titleText, titleStyle],
  );
  const subtitleMetrics = useMemo(
    () => TextMetrics.measureText(subtitleText, subtitleStyle),
    [subtitleText, subtitleStyle],
  );

  // Vertical layout: padding → title → gap → subtitle → image(fill) → button → padding
  const titleH = titleMetrics.height;
  const subtitleH = subtitleMetrics.height;
  const buttonAreaH = btnH;

  // Reserved title-row height. When one card's title wraps to more lines
  // than the other (both now share the same font size, but text length still
  // differs), reserving each card's own titleH would push its subtitle up
  // higher than the other card's — using the shared (tallest) height for
  // both keeps the subtitles vertically aligned across cards.
  const titleAreaH = sharedTitleAreaHeight ?? titleH;

  // Image gets remaining vertical space
  const usedH =
    paddingTop +
    titleAreaH +
    titleSubGap +
    subtitleH +
    titleSubGap +
    imageBottomGap +
    buttonAreaH +
    paddingBottom;
  const imageAvailH = Math.max(0, cardHeight - usedH);
  const imageAvailW = contentWidth * 0.92;

  // Scale image to fit available space
  const imageLayout = useMemo(() => {
    if (!featureTexture || imageAvailH <= 0) return null;
    const ar = featureTexture.width / featureTexture.height;
    let w, h;
    if (imageAvailH * ar <= imageAvailW) {
      h = imageAvailH;
      w = h * ar;
    } else {
      w = imageAvailW;
      h = w / ar;
    }
    const scale = tier.imageScale || 1;
    return { width: w * scale, height: h * scale };
  }, [featureTexture, imageAvailH, imageAvailW, tier]);

  // Y positions (all relative to card top-left origin)
  const cx = cardWidth / 2;
  // Title is vertically centered within the shared reserved area — so a
  // single-line title sits centered against a two-line title on the other
  // card, rather than pinned to the top of a taller-than-needed block.
  const titleY = paddingTop + titleAreaH / 2;
  const subtitleY = paddingTop + titleAreaH + titleSubGap + subtitleH / 2;
  // imageY = center of the available space region (between subtitle bottom and button top)
  const imageRegionTop =
    paddingTop + titleAreaH + titleSubGap + subtitleH + titleSubGap;
  const imageRegionBottom =
    cardHeight - paddingBottom - buttonAreaH - imageBottomGap;
  const imageY =
    (imageRegionTop + imageRegionBottom) / 2 +
    cardHeight * (0.015 + (tier.imageYOffset || 0));
  const buttonY = cardHeight - paddingBottom - buttonAreaH / 2;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleBuyClick = useCallback(() => {
    if (!canAfford || isProcessingRef.current) return;
    isProcessingRef.current = true;

    soundManager.play(SOUND_NAME.button_click);
    if (buyButtonContainerRef.current)
      triggerScalePress(buyButtonContainerRef.current);

    onBuy(tier);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);
  }, [canAfford, onBuy, tier]);

  return (
    <Container name={`buy_card_${tier.key}`}>
      {/* Card background — popup_frame_bonus texture */}
      {cardBgTexture && (
        <Sprite
          texture={cardBgTexture}
          width={cardWidth}
          height={cardHeight}
          anchor={0}
        />
      )}

      {/* Title */}
      <Text
        text={titleText}
        style={titleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={titleY}
      />

      {/* Subtitle */}
      <Text
        text={subtitleText}
        style={subtitleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={subtitleY}
      />

      {/* Feature image */}
      {imageLayout && featureTexture && (
        <Sprite
          texture={featureTexture}
          anchor={0.5}
          roundPixels={true}
          x={cx}
          y={imageY}
          width={imageLayout.width}
          height={imageLayout.height}
        />
      )}

      {/* BUY button */}
      {buttonTexture && (
        <Container
          ref={buyButtonContainerRef}
          name={`buy_button_${tier.key}`}
          x={cx}
          y={buttonY}
        >
          <Sprite
            ref={buttonRef}
            texture={
              canAfford
                ? buttonTexture
                : (buttonDisabledTexture ?? buttonTexture)
            }
            anchor={0.5}
            width={btnW}
            height={btnH}
            eventMode={canAfford ? "static" : "none"}
            cursor={canAfford ? "pointer" : "default"}
            onpointertap={handleBuyClick}
            onpointerover={canAfford ? handlePointerOver : null}
            onpointerout={canAfford ? handlePointerOut : null}
          />
          {/* BUY text */}
          <Text
            text={t("common:buy").toUpperCase()}
            style={buyTextStyle}
            anchor={{ x: 0.5, y: 1 }}
            roundPixels={true}
            x={0}
            y={-btnH * 0.001}
            eventMode="none"
          />
          {/* Price text */}
          <Text
            text={priceText}
            style={priceTextStyle}
            anchor={{ x: 0.5, y: 0 }}
            roundPixels={true}
            x={0}
            y={btnH * 0.001}
            eventMode="none"
            alpha={0.6}
          />
        </Container>
      )}
    </Container>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// BuyBonusPopup — full-screen popup with two bonus tier cards
// ═════════════════════════════════════════════════════════════════════════════
const BuyBonusPopup = () => {
  const dispatch = useDispatch();
  const {
    dimensions,
    device,
    orientation,
    symbolScale,
    isMobileSmall,
    getResponsiveConfig,
    uiSymbolSize,
  } = useDimensions();
  const { handleSpin } = useReelControl();

  const isOpen = useSelector(selectBonusBuyActive);
  const currentBet = useSelector((state) => state.game.bet);
  const currency = useSelector(selectCurrency);
  const balance = useSelector(selectBalance);
  const bonusBuyMultiplier = useSelector(selectBonusBuyMultiplier);
  const bonusBuyExtraMultiplier = useSelector(selectBonusBuyExtraMultiplier);

  // Build bonus tiers with API-driven multipliers (fallback to config defaults)
  const BONUS_TIERS = useMemo(
    () =>
      BONUS_TIER_CONFIGS.map((config) => ({
        ...config,
        multiplier:
          config.featureLevel === "1"
            ? bonusBuyMultiplier
            : bonusBuyExtraMultiplier,
      })),
    [bonusBuyMultiplier, bonusBuyExtraMultiplier],
  );

  // Spin guards
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);
  const buyConfirmedRef = useRef(false);
  const selectedTierRef = useRef(null);

  // Scroll state (mobile portrait only)
  const [scrollY, setScrollY] = useState(0);
  const isDragging = useRef(false);
  const lastTouchY = useRef(0);
  const scrollContentRef = useRef(null);
  const viewportMaskRef = useRef(null);

  // Close button hover/press effects
  const {
    buttonRef: closeButtonRef,
    handlePointerOver: closePointerOver,
    handlePointerOut: closePointerOut,
    triggerPress: closeTriggerPress,
    cleanupFilters: closeCleanupFilters,
  } = useButtonBrightness();

  useEffect(() => () => closeCleanupFilters(), [closeCleanupFilters]);

  // ── Responsive scale (symbolScale × deviceMultiplier) ──────────────────
  const popupScale = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const cfg =
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice]?.[orientation] ||
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice] ||
      POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP];
    const dm = cfg?.deviceMultiplier ?? 1.0;
    return symbolScale * dm;
  }, [device, orientation, symbolScale, isMobileSmall]);

  // ── Card dimensions (pinned to original 461×611 reference — texture scales to fit) ────
  const cardWidth = 461 * popupScale;
  const cardHeight = 611 * popupScale;

  // ── Shared title/description font sizes — both cards use the smaller of
  // the two "would-fit" sizes, so a long title or description on one card
  // doesn't make it look mismatched against a short (unshrunk) one on the
  // other. ────────────────────────────────────────────────────────────────
  const { t } = useTranslation();
  const cardContentWidth = useMemo(() => {
    const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio;
    return cardWidth - padding * 2;
  }, [cardWidth]);

  const titleFontSize = useMemo(() => {
    const idealTitleFontSize =
      Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToWidth(
          t(config.titleKey).toUpperCase(),
          idealTitleFontSize,
          cardContentWidth,
          "bold",
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t]);

  const subtitleFontSize = useMemo(() => {
    const idealSubtitleFontSize =
      Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToWidth(
          t(config.descKey),
          idealSubtitleFontSize,
          cardContentWidth,
          "500",
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t]);

  // ── Shared title-row height — both cards reserve the taller of the two
  // titles' wrapped heights (at the shared font size above), so if one
  // title wraps to more lines than the other, the descriptions below still
  // line up at the same Y instead of the shorter title's card sitting higher. ──
  const titleAreaHeight = useMemo(() => {
    const heights = BONUS_TIER_CONFIGS.map((config) => {
      const style = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: cardContentWidth,
      });
      return TextMetrics.measureText(
        t(config.titleKey).toUpperCase(),
        style,
      ).height;
    });
    return Math.max(...heights);
  }, [titleFontSize, cardContentWidth, t]);

  // ── Center coordinates ────────────────────────────────────────────────
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const configs = POPUP_CONFIG.DEVICE_CONFIGS;
    if (realDevice === PixiSettings.DEVICE.DESKTOP) {
      return configs[PixiSettings.DEVICE.DESKTOP] || {};
    }
    return configs[realDevice]?.[orientation] || {};
  }, [device, orientation, isMobileSmall]);

  // ── Bet selector scale (tweakable independently from cards) ────────────
  const betSelectorScale =
    popupScale *
    (deviceConfig.betSelectorScaleRatio ?? POPUP_CONFIG.betSelectorScaleRatio);

  const centerX = dimensions.width / 2;
  const centerY =
    dimensions.height / 2 + (deviceConfig.yOffset || 0) * cardHeight;

  // ── Layout mode (column only for mobile portrait, row for everything else) ──
  const isColumnLayout =
    orientation === PixiSettings.ORIENTATION.PORTRAIT &&
    (device === PixiSettings.DEVICE.MOBILE || isMobileSmall);

  const cardGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.cardGapPortraitRatio
      : POPUP_CONFIG.cardGapRatio);

  // ── Bet selector position (above cards) ───────────────────────────────
  const betSelectorGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.betSelectorGapPortraitRatio
      : POPUP_CONFIG.betSelectorGapRatio);
  const betSelectorY = useMemo(() => {
    if (isColumnLayout) {
      return centerY - cardHeight - cardGap / 2 - betSelectorGap;
    }
    return centerY - cardHeight / 2 - betSelectorGap;
  }, [centerY, cardHeight, cardGap, betSelectorGap, isColumnLayout]);

  // ── Close button size (same as ButtonSound / generic UI buttons) ───────
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );
  const closeButtonSizeMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.DESKTOP) return 0.8;
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT &&
      !isMobileSmall
    )
      return 0.45;
    return 0.5;
  }, [device, orientation, isMobileSmall]);
  const closeButtonSize = closeButtonConfig.width;

  // ── Close button position (mirrors ButtonSound padding per device) ──────
  const MAX_TOOLBAR_WIDTH = 1920;
  const closeButtonPos = useMemo(() => {
    const buttonVisualSize = closeButtonSize * closeButtonSizeMultiplier;

    // Y aligned with BET title (betSelectorY + label offset above the bet background)
    const betLabelY = betSelectorY - (popupScale * 110) / 2 - popupScale * 15;

    if (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        const closeYOffset = isMobileSmall
          ? -cardHeight * 0.05
          : -cardHeight * 0.07;
        const closeXMultiplier = isMobileSmall ? 1.4 : 1.2;
        return {
          x: dimensions.width - buttonVisualSize * closeXMultiplier,
          y: betLabelY + buttonVisualSize * 0.5 + closeYOffset,
        };
      }
      // Mobile landscape — position to the right of cards, halfway to toolbar edge
      const card2RightEdge = centerX + cardGap / 2 + cardWidth;
      const toolbarWidth = Math.min(dimensions.width, uiSymbolSize * 10);
      const toolbarEdge = (dimensions.width + toolbarWidth) / 2;
      return {
        x: (card2RightEdge + toolbarEdge) / 2,
        y: closeButtonSize * 0.8,
      };
    }

    // Tablet & Desktop — toolbar capped at 1920px
    const toolbarWidth = Math.min(dimensions.width, MAX_TOOLBAR_WIDTH);
    const toolbarOffset = (dimensions.width - toolbarWidth) / 2;

    if (device === PixiSettings.DEVICE.TABLET) {
      return {
        x: toolbarOffset + toolbarWidth - closeButtonSize * 0.8,
        y: closeButtonSize * 0.8,
      };
    }
    // Desktop
    return {
      x: toolbarOffset + toolbarWidth - closeButtonSize,
      y: closeButtonSize * 1.4,
    };
  }, [
    dimensions.width,
    device,
    orientation,
    isMobileSmall,
    closeButtonSize,
    closeButtonSizeMultiplier,
    uiSymbolSize,
    betSelectorY,
    popupScale,
    centerX,
    cardGap,
    cardWidth,
    cardHeight,
  ]);

  // ── Card positions (row in landscape, column in portrait) ─────────────
  const card1Pos = useMemo(() => {
    if (isColumnLayout) {
      return {
        x: centerX - cardWidth / 2,
        y: centerY - cardHeight - cardGap / 2,
      };
    }
    return {
      x: centerX - cardGap / 2 - cardWidth,
      y: centerY - cardHeight / 2,
    };
  }, [centerX, centerY, cardWidth, cardHeight, cardGap, isColumnLayout]);

  const card2Pos = useMemo(() => {
    if (isColumnLayout) {
      return {
        x: centerX - cardWidth / 2,
        y: centerY + cardGap / 2,
      };
    }
    return {
      x: centerX + cardGap / 2,
      y: centerY - cardHeight / 2,
    };
  }, [centerX, centerY, cardWidth, cardHeight, cardGap, isColumnLayout]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (isClosing) return;
    soundManager.play(SOUND_NAME.button_click);
    if (closeButtonRef.current) closeTriggerPress();
    setIsClosing(true);
  }, [isClosing, closeButtonRef, closeTriggerPress]);

  const handleBuy = useCallback(
    (tier) => {
      const cost = currentBet * tier.multiplier;
      if (balance < cost) return;

      // Spin guards
      const hasWin = currentRound?.win && currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin && spinComplete && !counterAnimationFinished;
      if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying)
        return;

      // Mark confirmed and store selected tier for spin trigger on close
      buyConfirmedRef.current = true;
      selectedTierRef.current = tier;
      setIsClosing(true);
    },
    [
      currentBet,
      balance,
      currentRound,
      spinComplete,
      counterAnimationFinished,
      isSpinning,
      criticalAnimationPlaying,
    ],
  );

  // ── Scroll (mobile portrait only) ────────────────────────────────────────
  // Max scroll = how far card2's bottom overshoots the screen
  const maxScrollY = isColumnLayout
    ? Math.max(0, card2Pos.y + cardHeight - dimensions.height + 40)
    : 0;

  const handleScrollPointerDown = useCallback(
    (e) => {
      if (!isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      isDragging.current = true;
      lastTouchY.current = e.data.global.y;
    },
    [isColumnLayout],
  );

  const handleScrollPointerMove = useCallback(
    (e) => {
      if (!isDragging.current || !isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      e.stopPropagation();
      const currentY = e.data.global.y;
      const delta = (lastTouchY.current - currentY) * 2.0;
      lastTouchY.current = currentY;
      setScrollY((prev) => Math.max(0, Math.min(prev + delta, maxScrollY)));
    },
    [isColumnLayout, maxScrollY],
  );

  const handleScrollPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Reset scroll when popup closes or layout changes
  useEffect(() => {
    if (!isOpen) setScrollY(0);
  }, [isOpen]);
  useEffect(() => {
    setScrollY(0);
  }, [isColumnLayout]);

  // Apply viewport mask to clip scrollable content when scrolling is needed
  useEffect(() => {
    if (scrollContentRef.current) {
      if (isColumnLayout && maxScrollY > 0 && viewportMaskRef.current) {
        scrollContentRef.current.mask = viewportMaskRef.current;
      } else {
        scrollContentRef.current.mask = null;
      }
    }
  }, [isColumnLayout, maxScrollY]);

  // Wheel scroll handler (desktop)
  const handleScrollWheel = useCallback(
    (event) => {
      if (!isColumnLayout) return;
      const scrollSpeed = 30;
      const newScrollY =
        scrollY + (event.deltaY > 0 ? scrollSpeed : -scrollSpeed);
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));
      if (clampedScrollY !== scrollY) {
        event.preventDefault();
        event.stopPropagation();
        setScrollY(clampedScrollY);
      }
    },
    [isColumnLayout, scrollY, maxScrollY],
  );

  // ── Open animation (overlay fade → content fade, no scale) ─────────────
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      // Fade in content (slight delay after overlay)
      if (popupContentRef.current) {
        popupContentRef.current.alpha = 0;
        anime({
          targets: popupContentRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }
    }
  }, [isOpen, isClosing]);

  // ── Close animation (content fade → overlay fade) ─────────────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    // Fade out content
    if (popupContentRef.current) {
      contentAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    // Fade out overlay (slight delay after content)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeBonusBuy());
        setIsClosing(false);

        // Trigger spin after popup is fully closed
        if (buyConfirmedRef.current) {
          buyConfirmedRef.current = false;
          const tier = selectedTierRef.current;
          selectedTierRef.current = null;
          handleSpin({
            purchasedFeature: "bonus_buy",
            purchasedLevel: tier?.featureLevel || "1",
            bonusCost: currentBet * tier.multiplier,
          });
        }
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch, handleSpin, currentBet]);

  // ── Draw overlay ──────────────────────────────────────────────────────
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  // ── Early return ──────────────────────────────────────────────────────
  if (!isOpen && !isClosing) return null;

  return (
    <Container
      name="buy_bonus_popup"
      zIndex={10000}
      sortableChildren
      eventMode="static"
    >
      {/* Dark overlay — catches stray clicks, blocks game underneath */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        zIndex={0}
      />

      {/* Animated content container */}
      <Container name="popup_content" ref={popupContentRef} zIndex={1}>
        {/* Scroll event catcher — fixed in screen space, hitArea never moves */}
        <Container
          name="scroll_event_catcher"
          eventMode={isColumnLayout ? "static" : "passive"}
          onpointerdown={handleScrollPointerDown}
          onpointermove={handleScrollPointerMove}
          onpointerup={handleScrollPointerUp}
          onpointercancel={handleScrollPointerUp}
          onpointerleave={handleScrollPointerUp}
          onwheel={handleScrollWheel}
          {...(isColumnLayout
            ? {
                hitArea: new PIXI.Rectangle(
                  0,
                  0,
                  dimensions.width,
                  dimensions.height,
                ),
              }
            : {})}
        >
          {/* Viewport mask — clips scrollable content to visible area */}
          {isColumnLayout && maxScrollY > 0 && (
            <Graphics
              ref={viewportMaskRef}
              name="viewport_mask"
              renderable={false}
              draw={(g) => {
                g.clear();
                g.beginFill(0x000000, 1);
                g.drawRect(0, 0, dimensions.width, dimensions.height);
                g.endFill();
              }}
            />
          )}

          {/* Scrollable content — only this moves */}
          <Container
            ref={scrollContentRef}
            name="scrollable_content"
            y={isColumnLayout ? -scrollY : 0}
          >
            {/* Bet selector — above cards */}
            <BonusBetSelector
              popupScale={betSelectorScale}
              position={{ x: centerX, y: betSelectorY }}
            />

            {/* Card 1 — Zeus Respins */}
            <Container x={card1Pos.x} y={card1Pos.y}>
              <BuyCard
                tier={BONUS_TIERS[0]}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                currentBet={currentBet}
                currency={currency}
                canAfford={balance >= currentBet * BONUS_TIERS[0].multiplier}
                onBuy={handleBuy}
                sharedTitleFontSize={titleFontSize}
                sharedSubtitleFontSize={subtitleFontSize}
                sharedTitleAreaHeight={titleAreaHeight}
              />
            </Container>

            {/* Card 2 — Zeus Respins Extra */}
            <Container x={card2Pos.x} y={card2Pos.y}>
              <BuyCard
                tier={BONUS_TIERS[1]}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                currentBet={currentBet}
                currency={currency}
                canAfford={balance >= currentBet * BONUS_TIERS[1].multiplier}
                onBuy={handleBuy}
                sharedTitleFontSize={titleFontSize}
                sharedSubtitleFontSize={subtitleFontSize}
                sharedTitleAreaHeight={titleAreaHeight}
              />
            </Container>
          </Container>
        </Container>

        {/* Close button — outside scroll area, always fixed */}
        <Sprite
          ref={closeButtonRef}
          name="popup_close_button"
          texture={getNewUITexture("button_x")}
          anchor={0.5}
          width={closeButtonSize * closeButtonSizeMultiplier}
          height={closeButtonSize * closeButtonSizeMultiplier}
          x={closeButtonPos.x}
          y={closeButtonPos.y}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClose}
          onpointerover={closePointerOver}
          onpointerout={closePointerOut}
        />
      </Container>
    </Container>
  );
};

export default BuyBonusPopup;
